import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { deleteWorkAreaEnvVar, listWorkAreaEnvVars, setWorkAreaEnvVar } from '@/lib/api';
import type { CodebaseResponse, WorkAreaEnvVar } from '@/lib/api';

interface WorkAreaEnvVarsPanelProps {
  workAreaId: string;
  memberCodebaseIds: string[];
  codebaseById: Map<string, CodebaseResponse>;
}

function codebaseLabel(codebase: CodebaseResponse): string {
  return codebase.github_repo_full_name ?? codebase.name;
}

function formatDate(value: string): string {
  return value.slice(0, 19).replace('T', ' ');
}

function scopeLabel(envVar: WorkAreaEnvVar, codebaseById: Map<string, CodebaseResponse>): string {
  const codebaseId = envVar.scope.codebaseId;
  if (!codebaseId) return 'Work Area default';
  const codebase = codebaseById.get(codebaseId);
  return `${codebase ? codebaseLabel(codebase) : codebaseId} override`;
}

export function WorkAreaEnvVarsPanel({
  workAreaId,
  memberCodebaseIds,
  codebaseById,
}: WorkAreaEnvVarsPanelProps): React.ReactElement {
  const queryClient = useQueryClient();
  const [key, setKey] = useState('');
  const [value, setValue] = useState('');
  const [scopeCodebaseId, setScopeCodebaseId] = useState('');
  const [message, setMessage] = useState<string | null>(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['workAreaEnvVars', workAreaId],
    queryFn: () => listWorkAreaEnvVars(workAreaId),
  });

  const envVars = useMemo(() => {
    const sorted = Array.from(data?.vars ?? []);
    sorted.sort((a, b) => {
      const keyOrder = a.key.localeCompare(b.key);
      if (keyOrder !== 0) return keyOrder;
      if (a.scope.codebaseId === b.scope.codebaseId) return 0;
      if (a.scope.codebaseId === null) return -1;
      if (b.scope.codebaseId === null) return 1;
      return a.scope.codebaseId.localeCompare(b.scope.codebaseId);
    });
    return sorted;
  }, [data]);

  const defaultKeys = useMemo(() => {
    const keys = new Set<string>();
    for (const envVar of envVars) {
      if (envVar.scope.codebaseId === null) {
        keys.add(envVar.key);
      }
    }
    return keys;
  }, [envVars]);

  const memberCodebases = useMemo(() => {
    const codebases: CodebaseResponse[] = [];
    for (const codebaseId of memberCodebaseIds) {
      const codebase = codebaseById.get(codebaseId);
      if (codebase) {
        codebases.push(codebase);
      }
    }
    codebases.sort((a, b) => codebaseLabel(a).localeCompare(codebaseLabel(b)));
    return codebases;
  }, [codebaseById, memberCodebaseIds]);

  const setMutation = useMutation({
    mutationFn: (input: { key: string; value: string; codebaseId: string | null }) =>
      setWorkAreaEnvVar(workAreaId, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['workAreaEnvVars', workAreaId] });
      setKey('');
      setValue('');
      setScopeCodebaseId('');
      setMessage('Variable saved.');
    },
    onError: err => {
      setMessage(err instanceof Error ? err.message : 'Failed to save variable.');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: ({ key: deleteKey, codebaseId }: { key: string; codebaseId: string | null }) =>
      deleteWorkAreaEnvVar(workAreaId, deleteKey, codebaseId ?? undefined),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['workAreaEnvVars', workAreaId] });
      setMessage('Variable deleted.');
    },
    onError: err => {
      setMessage(err instanceof Error ? err.message : 'Failed to delete variable.');
    },
  });

  function handleSave(): void {
    const trimmedKey = key.trim();
    if (!trimmedKey) {
      setMessage('Key is required.');
      return;
    }
    setMessage(null);
    setMutation.mutate({
      key: trimmedKey,
      value,
      codebaseId: scopeCodebaseId || null,
    });
  }

  return (
    <section className="space-y-3 border-t border-border pt-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-mono text-[11px] font-bold uppercase tracking-widest text-text-secondary">
            Environment variables · scoped to Work Area
          </h3>
          <div className="mt-1 text-[11px] text-text-tertiary">
            Values hidden. Codebase scope overrides default.
          </div>
        </div>
        {message && <span className="text-xs text-text-secondary">{message}</span>}
      </div>

      <div className="grid grid-cols-1 gap-2 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(12rem,0.8fr)_auto]">
        <label className="grid gap-1 text-sm">
          <span className="text-text-secondary">Key</span>
          <input
            type="text"
            value={key}
            onChange={e => {
              setKey(e.target.value);
            }}
            className="min-w-0 rounded-md border border-border bg-surface px-2 py-1.5 text-sm text-text-primary"
            placeholder="GITHUB_TOKEN"
          />
        </label>
        <label className="grid gap-1 text-sm">
          <span className="text-text-secondary">Value</span>
          <input
            type="password"
            value={value}
            onChange={e => {
              setValue(e.target.value);
            }}
            className="min-w-0 rounded-md border border-border bg-surface px-2 py-1.5 text-sm text-text-primary"
            placeholder="Write-only"
          />
        </label>
        <label className="grid gap-1 text-sm">
          <span className="text-text-secondary">Scope</span>
          <select
            value={scopeCodebaseId}
            onChange={e => {
              setScopeCodebaseId(e.target.value);
            }}
            className="min-w-0 rounded-md border border-border bg-surface px-2 py-1.5 text-sm text-text-primary"
          >
            <option value="">Work Area default</option>
            {memberCodebases.map(codebase => (
              <option key={codebase.id} value={codebase.id}>
                {codebaseLabel(codebase)} override
              </option>
            ))}
          </select>
        </label>
        <div className="flex items-end">
          <Button
            type="button"
            size="sm"
            onClick={handleSave}
            disabled={!key.trim() || setMutation.isPending}
          >
            Add variable
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="py-4 text-sm text-text-secondary">Loading variables…</div>
      ) : isError ? (
        <div className="rounded-md border border-error/40 bg-error/10 p-3 text-sm text-error">
          Failed to load environment variables.
        </div>
      ) : envVars.length === 0 ? (
        <div className="rounded-md border border-border bg-surface px-3 py-5 text-sm text-text-secondary">
          No environment variables.
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-border bg-surface-inset">
          <table className="w-full table-fixed text-sm">
            <thead>
              <tr className="border-b border-border text-left">
                <th className="w-[32%] px-3 py-2 font-mono text-[9.5px] font-semibold uppercase tracking-widest text-text-tertiary">
                  Key
                </th>
                <th className="w-[32%] px-3 py-2 font-mono text-[9.5px] font-semibold uppercase tracking-widest text-text-tertiary">
                  Scope
                </th>
                <th className="w-[24%] px-3 py-2 font-mono text-[9.5px] font-semibold uppercase tracking-widest text-text-tertiary">
                  Updated
                </th>
                <th className="w-[12%] px-3 py-2 text-right font-mono text-[9.5px] font-semibold uppercase tracking-widest text-text-tertiary">
                  Delete
                </th>
              </tr>
            </thead>
            <tbody>
              {envVars.map(envVar => {
                const codebaseId = envVar.scope.codebaseId;
                const overridesDefault = codebaseId !== null && defaultKeys.has(envVar.key);
                return (
                  <tr
                    key={`${envVar.key}:${codebaseId ?? 'default'}`}
                    className="border-b border-border/60 last:border-0"
                  >
                    <td className="truncate px-3 py-2.5 font-mono text-[11.5px] font-semibold text-flame-hi">
                      {envVar.key}
                    </td>
                    <td className="truncate px-3 py-2.5 font-mono text-[11px] text-text-secondary">
                      {scopeLabel(envVar, codebaseById)}
                      {overridesDefault && (
                        <span className="ml-2 rounded border border-warning/25 bg-[oklch(0.78_0.16_75/0.12)] px-1.5 py-0.5 font-mono text-[9.5px] font-bold uppercase tracking-widest text-warning">
                          supersedes
                        </span>
                      )}
                    </td>
                    <td className="truncate px-3 py-2.5 font-mono text-[11px] text-text-tertiary">
                      {formatDate(envVar.updatedAt)}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-xs"
                        title="Delete variable"
                        aria-label={`Delete ${envVar.key} ${scopeLabel(envVar, codebaseById)}`}
                        onClick={(): void => {
                          setMessage(null);
                          deleteMutation.mutate({ key: envVar.key, codebaseId });
                        }}
                        disabled={deleteMutation.isPending}
                      >
                        <Trash2 className="size-3" />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
